import type { ReplyGrant } from "@sss/shared";

const STORAGE_KEY = "sss.replyGrant";

export interface StoredReplyGrant {
  grant: ReplyGrant;
  /**
   * The address this browser just verified. Offered back to the API as the
   * read-receipt target for the reply, which is the only address the grant
   * will accept for one — see applyReplyGrant in api/src/routes/secrets.ts.
   */
  recipientEmail: string;
}

/**
 * Where a reply grant lives between the retrieval page and the reply
 * composer.
 *
 * `sessionStorage`, deliberately, and it is the one thing this app stores
 * in a browser. Not the URL fragment, which belongs to the decryption
 * passphrase and is emptied on load (lib/linkKey.ts). Not the query string,
 * where it would reach the server on every navigation and land in history.
 * Not React state alone, which a reload would throw away — and a reload is
 * unrecoverable here, because the secret that produced the grant is usually
 * a one-time one that no longer exists.
 *
 * What is stored is not a key and not a secret: it authorizes one creation,
 * addressed by the server to the person who sent the original. It is scoped
 * to this tab, cleared the moment the reply is sent, and expires on the
 * server regardless.
 *
 * Every access is wrapped: sessionStorage throws in some privacy modes, and
 * a browser that won't store this should simply not offer a reply rather
 * than break the page that is showing someone their secret.
 */
export function storeReplyGrant(value: StoredReplyGrant): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Nothing to do — the reply page will find nothing and say so.
  }
}

export function loadReplyGrant(): StoredReplyGrant | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredReplyGrant;
    if (!parsed?.grant?.token || !parsed.grant.replyTo) return null;
    // The server is the authority on expiry; this only avoids walking
    // someone through composing a reply that is already refused.
    if (parsed.grant.expiresAt * 1000 <= Date.now()) {
      clearReplyGrant();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearReplyGrant(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Same as above: an unusable store is not an error worth surfacing.
  }
}
